import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ActiveCall as ActiveCallData,
  ChatMessage,
  MediaGrant,
  SessionContext,
} from '../api.ts';
import { sendSessionMessage, sessionContext, sessionMessages } from '../api.ts';
import { type CallEvent, type CallSession, connectToCall } from '../media.ts';
import { preferenceEnabled } from '../preferences.ts';

/**
 * Conversazione in corso, su uno dei tre canali.
 *
 * Cosa cambia fra i tre non è l'interfaccia: è **cosa si sa di chi c'è
 * dall'altra parte** (sezione 5.6).
 *
 *  - **audio anonimo**: niente. Non sappiamo chi è, non sappiamo se ha già
 *    chiamato, non esiste uno storico da consultare. Non è una mancanza
 *    dell'interfaccia, è il prodotto — e va detto al volontario invece di
 *    lasciare un riquadro vuoto;
 *  - **video**: c'è un account, quindi c'è un'etichetta e c'è ciò che è stato
 *    scritto nelle conversazioni precedenti;
 *  - **chat**: come il video, più la conversazione in corso.
 *
 * Su nessuno dei tre compare il numero di telefono. Nemmeno mascherato: tre
 * cifre e un prefisso bastano a riconoscere una persona che si conosce già, ed
 * è esattamente lo scenario da cui l'anonimato protegge.
 *
 * Il cronometro **non sta in una regione live**: verrebbe riletto a ogni
 * secondo e renderebbe impossibile seguire la conversazione (sezione 7bis).
 */

type Props = {
  call: ActiveCallData;
  media: MediaGrant | undefined;
  emergencyNumber: string | undefined;
  /** Il titolo riceve il focus quando questa schermata subentra. */
  headingRef?: React.RefObject<HTMLElement | null>;
  onEnd: () => void;
  /**
   * "Allerta supervisore" (sezione 5.9).
   *
   * Il pannello lo apre la console, non questo componente: la conversazione
   * resta montata sotto — il media non si interrompe per aprire una schermata
   * di testo — e chi coordina l'una e l'altra è la stessa pagina.
   */
  onEscalate?: () => void;
  /** Dove il fuoco torna quando il pannello della supervisione si chiude. */
  escalateButtonRef?: React.RefObject<HTMLButtonElement | null>;
};

/** Ritmo con cui si rileggono i messaggi. Come quello della pagina dell'utente. */
const CHAT_POLL_MS = 2000;

/**
 * Quanto può essere lungo un messaggio, iniettato dalla build.
 *
 * Il server ha `MESSAGE_MAX_LENGTH` in configurazione: scriverlo anche qui
 * significava che abbassando la variabile i due client avrebbero lasciato
 * scrivere messaggi che il server rifiuta.
 */
declare const __MESSAGE_MAX_LENGTH__: number | undefined;
const MESSAGE_MAX_LENGTH =
  typeof __MESSAGE_MAX_LENGTH__ === 'number' ? __MESSAGE_MAX_LENGTH__ : 2000;

export function ActiveCall({
  call,
  media,
  emergencyNumber,
  headingRef,
  onEnd,
  onEscalate,
  escalateButtonRef,
}: Props) {
  const { t } = useTranslation();
  const sinkRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const sessionRef = useRef<CallSession>(null);
  const [connection, setConnection] = useState<CallEvent['type']>('reconnecting');
  /**
   * Quante volte si è chiesto di ricollegarsi.
   *
   * È l'unica dipendenza dell'effetto di collegamento che cambia per volontà
   * di chi risponde, e serve proprio a farlo rieseguire: senza, un
   * collegamento fallito restava fallito fino alla fine della conversazione e
   * l'unica via d'uscita era chiuderla.
   */
  const [attempt, setAttempt] = useState(0);
  /**
   * Il microfono bloccato è uno stato **suo**, non uno stato del collegamento.
   *
   * Tenerlo dentro `connection` faceva dire "collegamento in corso" a chi era
   * collegatissimo e aveva solo il microfono spento dal browser: una riga falsa
   * proprio nel momento in cui serviva quella vera (R11).
   */
  const [microphoneBlocked, setMicrophoneBlocked] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [muted, setMuted] = useState(false);
  /**
   * Lo stato che si è **chiesto**, che non è lo stato che si è ottenuto.
   *
   * Il pulsante esiste da quando la schermata compare; la sessione media no —
   * `connectToCall` importa il client, apre la room e accende il microfono, e
   * fino a quando non ha finito `sessionRef.current` è `null`. In quella
   * finestra — circa un secondo, cioè **esattamente** il momento in cui chi
   * risponde tocca "disattiva il microfono" — `sessionRef.current?.setMuted()`
   * non chiamava niente e non lo diceva a nessuno: l'etichetta passava a
   * "riattiva il microfono" e dall'altra parte si continuava a sentire tutto.
   *
   * Adesso la richiesta si conserva e si applica appena la sessione esiste.
   */
  const wantedMuteRef = useRef(false);
  /**
   * Il microfono è rimasto acceso contro la volontà di chi risponde.
   *
   * `void session.setMuted(next)` buttava via il rifiuto della promessa: se
   * spegnere il microfono falliva — dispositivo occupato, permesso revocato a
   * chiamata in corso — la schermata continuava a dire che era spento. Un
   * microfono che si crede spento è la sola cosa qui che non può restare
   * implicita.
   */
  const [muteFailed, setMuteFailed] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [remoteVideo, setRemoteVideo] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [context, setContext] = useState<SessionContext | null>(null);

  const isChat = call.channel === 'chat';
  const isVideo = call.channel === 'video';

  useEffect(() => {
    const startedAt = call.startedAt ? new Date(call.startedAt).getTime() : Date.now();
    const handle = setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => clearInterval(handle);
  }, [call.startedAt]);

  /**
   * Chi c'è dall'altra parte, chiesto una volta sola per sessione.
   *
   * Dipende dall'**id**, non dall'oggetto: l'interrogazione periodica della
   * console ricrea `call` a ogni giro, e senza questo lo storico verrebbe
   * richiesto ogni due secondi (X5, la stessa trappola del token media).
   */
  const sessionId = call.sessionId;
  useEffect(() => {
    let cancelled = false;
    void sessionContext(sessionId)
      .then((result) => {
        if (!cancelled) setContext(result);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  /**
   * Spegne o riaccende il microfono, e dice il vero su cosa è successo.
   *
   * Senza sessione non si fa finta di niente: la richiesta resta in
   * `wantedMuteRef` e la applica il collegamento appena si stabilisce. Se
   * invece la sessione c'è e rifiuta, si torna indietro — l'etichetta e la
   * riga di stato tornano a dire "acceso", perché è quello che è — e si
   * avvisa.
   */
  const applyMute = useCallback(async (next: boolean): Promise<void> => {
    const session = sessionRef.current;
    if (!session) return;
    try {
      await session.setMuted(next);
      setMuteFailed(false);
    } catch {
      wantedMuteRef.current = false;
      setMuted(false);
      setMuteFailed(true);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` non si legge nel corpo, ma è l'unica ragione per cui l'effetto viene rieseguito — toglierlo lascerebbe per sempre un collegamento caduto, che è il difetto per cui la dipendenza esiste.
  useEffect(() => {
    const sink = sinkRef.current;
    // La chat non ha media: non c'è niente da collegare, e provarci
    // aprirebbe il microfono a chi ha scelto di scrivere.
    if (isChat || !media || !sink) return;

    /*
      Un collegamento superato non deve più poter scrivere lo stato di questo
      componente (BJ2, `docs/DECISIONI.md`): l'effetto può rieseguire —
      StrictMode lo fa apposta due volte a ogni montaggio, e "riprova"
      (`attempt`) lo fa apposta una volta di più — e la `disconnect()` della
      room vecchia è asincrona. Se nel frattempo arriva un evento dalla room
      vecchia (tipicamente proprio `disconnected`, la conseguenza naturale di
      quella disconnect) o la sua promessa di collegamento cade in ritardo,
      senza controllare `cancelled` in entrambi i punti sotto — il callback
      `onEvent` e il `.catch()` — sovrascrivevano lo stato della room nuova,
      magari già collegata e funzionante, con un falso "collegamento non
      riuscito".
    */
    let cancelled = false;
    void connectToCall(
      media,
      {
        sink,
        withVideo: isVideo,
        remoteVideo: remoteVideoRef.current,
        localVideo: localVideoRef.current,
        /*
          Il microfono non si pubblica acceso se nel frattempo è stato chiesto
          il contrario: accenderlo e rincorrerlo lascerebbe passare la voce per
          tutta la durata del collegamento — che su una videochiamata comprende
          il permesso della camera, cioè quanto ci mette una persona a
          rispondere a una finestra del browser.
        */
        muted: () => wantedMuteRef.current,
      },
      (event) => {
        if (cancelled) return;
        if (event.type === 'remote_video') {
          setRemoteVideo(event.present);
          return;
        }
        if (event.type === 'camera_blocked') {
          setCameraOn(false);
          setCameraBlocked(true);
          return;
        }
        if (event.type === 'microphone_blocked') {
          setMicrophoneBlocked(true);
          return;
        }
        setConnection(event.type);
      },
    )
      .then((session) => {
        if (cancelled) {
          void session.disconnect();
          return;
        }
        sessionRef.current = session;
        /*
          E si applica subito ciò che è stato chiesto mentre non c'era ancora
          niente a cui chiederlo: senza questa riga il microfono resta acceso e
          la schermata dice il contrario.
        */
        if (wantedMuteRef.current) void applyMute(true);
      })
      .catch(() => {
        if (!cancelled) setConnection('failed');
      });

    return () => {
      cancelled = true;
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session) void session.disconnect();
    };
    /*
      `applyMute` sta in `useCallback` con nessuna dipendenza, quindi la sua
      identità non cambia mai: aggiungerlo qui non fa **ricollegare** niente.
      Un ricollegamento a chiamata in corso chiuderebbe e riaprirebbe la room
      sotto una conversazione, e il vincolo 5 dice che una sessione attiva non
      si interrompe per nessun motivo.
    */
    /*
      `attempt` invece è lì **apposta**: è l'unica cosa che fa ricollegare, e la
      muove solo il pulsante "riprova". Ricollegarsi non è chiudere la
      sessione — la room resta la stessa, `onEnd` non viene chiamato — quindi
      il vincolo 5 è rispettato: non stiamo interrompendo niente, stiamo
      rimettendo in piedi qualcosa che era già caduto.
    */
  }, [media, isChat, isVideo, applyMute, attempt]);

  /**
   * Il collegamento non c'è: né in piedi, né in costruzione.
   *
   * `failed` e `disconnected` si disegnavano come "Collegamento in corso…" —
   * la riga diceva `connection === 'connected' ? … : …` e basta — quindi chi
   * restava senza voce vedeva per sempre una schermata che prometteva che
   * stava arrivando. Nel gruppo `call.*` non esisteva nemmeno una chiave per
   * il fallimento: il caso non era mai stato disegnato.
   */
  const lost = connection === 'failed' || connection === 'disconnected';

  /**
   * Si ritenta il **solo** collegamento.
   *
   * Non si chiude la sessione e non si avvisa la console: dall'altra parte c'è
   * ancora una persona, e il vincolo 5 dice che una sessione attiva non si
   * interrompe per nessun motivo — men che meno per un problema di rete di
   * questo lato.
   */
  const retryConnection = (): void => {
    setConnection('reconnecting');
    setAttempt((current) => current + 1);
  };

  const toggleMute = (): void => {
    const next = !wantedMuteRef.current;
    wantedMuteRef.current = next;
    setMuted(next);
    setMuteFailed(false);
    void applyMute(next);
  };

  const toggleCamera = (): void => {
    const next = !cameraOn;
    void sessionRef.current?.setCameraEnabled(next).then((applied) => setCameraOn(applied));
  };

  return (
    <section className="call" aria-labelledby="call-heading">
      {/* `h3`: il titolo di pagina è quello della console, e questa è una sezione. */}
      <h3
        id="call-heading"
        ref={headingRef as React.RefObject<HTMLHeadingElement> | undefined}
        tabIndex={-1}
      >
        {t(isChat ? 'call.title_chat' : isVideo ? 'call.title_video' : 'call.title')}
      </h3>

      <div className="call__panel">
        <p className="call__who">
          {!context || context.anonymous || !context.label
            ? /*
               * Su una chiamata anonima non c'è niente da dire, e lo si dice:
               * un riquadro vuoto sembrerebbe un difetto (sezione 5.6).
               */
              t('incoming.anonymous')
            : /*
               * L'etichetta scelta dalla persona, o quella generata dal
               * sistema. Nessun numero, nemmeno mascherato.
               */
              (context.label.custom ?? t('call.guest_label', { number: context.label.number }))}
        </p>
        {/* Elemento normale, aggiornato a mano: mai una regione live. */}
        <p className="call__timer">{t('call.duration', { value: formatElapsed(elapsed) })}</p>
        {isChat ? null : (
          // Il collegamento cambia di rado: annunciarlo non disturba. Tre
          // stati, non due: collegato, in corso, e non riuscito — che è quello
          // per cui questa riga esiste.
          <p className="muted" role="status">
            {connection === 'connected'
              ? t('call.connected')
              : lost
                ? t('call.connection_failed')
                : t('call.connecting')}
          </p>
        )}
        {isChat || !lost ? null : (
          /*
            Il pulsante compare solo quando c'è qualcosa da ritentare, e sta
            accanto alla riga che lo spiega. Non è distruttivo — non chiude
            niente — quindi è un pulsante normale, non uno `button--danger`.
          */
          <button type="button" className="button" onClick={retryConnection}>
            {t('call.reconnect')}
          </button>
        )}
      </div>

      {context?.anonymous ? <p className="muted">{t('call.anonymous_note')}</p> : null}

      {isVideo ? (
        <div className="call__stage">
          <video
            ref={remoteVideoRef}
            className="call__remote"
            autoPlay
            playsInline
            /* La voce arriva dall'elemento audio: due sorgenti sono un'eco. */
            muted
            hidden={!remoteVideo}
            aria-label={t('call.remote_video_label')}
          />
          {/*
            La stessa frase c'è già nella riga di stato qui sotto, che è la
            regione live: quella la **annuncia** quando la camera dall'altra
            parte si spegne a metà conversazione, cosa che un `<p>` che compare
            non fa. Questa è la copia per gli occhi, dentro il riquadro vuoto
            dove il video non c'è, ed è `aria-hidden` perché sentirsela leggere
            due volte di fila non aiuta nessuno.
          */}
          {remoteVideo ? null : (
            <p className="muted" aria-hidden="true">
              {t('call.no_remote_video')}
            </p>
          )}
          <video
            ref={localVideoRef}
            className="call__self"
            autoPlay
            playsInline
            muted
            hidden={!cameraOn}
            aria-label={t('call.self_video_label')}
          />
        </div>
      ) : null}

      {isChat ? <ChatThread sessionId={sessionId} context={context} /> : null}

      {/*
        I tre avvisi di questa conversazione sono **regioni montate e vuote**,
        e si riempiono quando c'è qualcosa da dire: nascevano insieme al proprio
        messaggio, che è il modo in cui una regione non viene annunciata (R10).
        Vuote escono dal disegno con `visually-hidden`, che ritaglia e non
        toglie dall'albero di accessibilità: con `hidden` la regione tornerebbe
        a essere osservata solo da quando ha già il testo dentro.

        Il microfono è l'unico `alert` dei tre, e la differenza non è di stile.
        Senza microfono la chiamata accettata non passa voce: è la sola cosa
        qui che non può aspettare la fine di ciò che lo screen reader sta
        leggendo. E due `alert` non convivono: negando i permessi di una
        videochiamata `connectToCall` emette `microphone_blocked` e, un attimo
        dopo, `camera_blocked` (`media.ts`) — un solo permesso del browser
        copre entrambi — e il secondo assertivo interrompe il primo. A perdersi
        sarebbe proprio questo.
      */}
      <p
        className={microphoneBlocked || muteFailed ? 'banner banner--danger' : 'visually-hidden'}
        role="alert"
        aria-live="assertive"
      >
        {/*
          Due cose nella stessa regione, e non è una scorciatoia: sono lo
          stesso fatto — il microfono non sta facendo quello che si crede —
          e due `alert` sulla stessa schermata si spengono a vicenda (AP1).
          Il permesso negato viene prima: senza di quello la chiamata non
          passa voce, e spegnerlo non è più il problema.
        */}
        {microphoneBlocked ? t('call.microphone_blocked') : muteFailed ? t('call.mute_failed') : ''}
      </p>

      {/*
        La camera negata si dice, come il microfono, ma **polite**: la
        conversazione prosegue con la sola voce, e il codice che la gestisce lo
        dice già (`media.ts`). Un assertivo qui interromperebbe la lettura per
        una cosa che non ferma niente — e spegnerebbe quello sopra.
      */}
      <p
        className={cameraBlocked ? 'banner banner--danger' : 'visually-hidden'}
        role="status"
        aria-live="polite"
      >
        {cameraBlocked ? t('call.camera_blocked') : ''}
      </p>

      {/*
        I comandi della conversazione stanno insieme, e su un telefono in
        verticale **non escono dallo schermo**.

        Erano tre pulsanti in mezzo al flusso: con una chat aperta — riquadro
        dei messaggi, casella di scrittura, avvisi — "chiudi" e "allerta
        supervisore" finivano sotto il bordo, e per raggiungerli bisognava
        scorrere con una persona in linea. Da qui in giù il gruppo resta
        appoggiato al fondo dello schermo finché non lo si raggiunge davvero
        (la regola sta nel foglio, sotto i 34rem: su schermo largo non serve e
        non c'è).
      */}
      <div className="call__controls">
        <div className="call__actions">
          {/*
          Il nome cambia, e `aria-pressed` **non** c'è.
          Averli entrambi faceva leggere a NVDA "Attiva il microfono, pulsante,
          premuto", cioè il contrario di quello che era (WCAG 4.1.2). Il nome
          dice già l'azione; lo stato lo dice la riga sotto ai pulsanti.
        */}
          {isChat ? null : (
            <button type="button" className="button" onClick={toggleMute}>
              {t(muted ? 'call.unmute' : 'call.mute')}
            </button>
          )}
          {isVideo ? (
            <button type="button" className="button" onClick={toggleCamera}>
              {t(cameraOn ? 'call.camera_off' : 'call.camera_on')}
            </button>
          ) : null}
          <button type="button" className="button button--danger-solid" onClick={onEnd}>
            {t(isChat ? 'call.end_chat' : 'call.end')}
          </button>
        </div>

        {/*
          "Il pulsante «Allerta supervisore» ha un nome accessibile chiaro ed è
          raggiungibile con pochi tab, non in fondo alla pagina" (sezione 7bis):
          sta subito sotto i comandi della conversazione, prima dello storico e
          prima di tutto il resto.

          Il supervisore **non entra** nella conversazione: apre un canale di solo
          testo, e questo pulsante non tocca il media (vincolo 4).
        */}
        {onEscalate ? (
          <button
            type="button"
            className="button button--wide button--danger"
            ref={escalateButtonRef}
            onClick={onEscalate}
          >
            {t('supervision.alert')}
          </button>
        ) : null}
      </div>
      {/*
        Una regione **persistente** il cui testo cambia, non un paragrafo che
        compare già pieno: una regione live montata con dentro il messaggio non
        viene letta (R10), e riattivando il microfono non si sentiva niente.
        Vale anche per la camera e per quella dell'altro.
      */}
      <p className="muted" role="status">
        {[
          muted ? t('call.muted') : '',
          /*
            Solo quando la camera è **spenta**, come per il microfono.

            Qui c'era `t(cameraOn ? 'call.camera_is_on' : 'call.camera_is_off')`
            e nessuna delle due chiavi esisteva: i18next restituisce la chiave
            quando non la trova, quindi a schermo si leggeva
            `call.camera_is_on` — dentro un `role="status"`, cioè letto anche
            ad alta voce.

            Le due chiavi che esistono, `call.camera_on` e `call.camera_off`,
            sono le etichette del **pulsante** ("Accendi la camera" / "Spegni
            la camera"): metterle qui avrebbe fatto dire alla riga di stato un
            comando invece di uno stato.

            E se ne aggiunge una sola, non due: in una videochiamata la camera
            accesa è la normalità, e annunciarla sarebbe rumore accanto a un
            pulsante che già dice "Spegni la camera". Si dice ciò che è fuori
            dall'ordinario, che è la stessa regola del microfono muto qui
            sopra.
          */
          isVideo && !cameraOn ? t('call.camera_is_off') : '',
          isVideo && !remoteVideo ? t('call.no_remote_video') : '',
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {/*
        La scorciatoia si annuncia solo se è accesa.
        Prometterne una che è stata spenta è X12 al contrario: chi l'ha
        disattivata continuava a leggere "premi Maiusc+C per chiudere".
      */}
      {preferenceEnabled('console.shortcuts') ? (
        <p className="muted">{t('call.keyboard_hint')}</p>
      ) : null}

      {emergencyNumber ? (
        <p className="banner banner--warn">{t('call.emergency', { number: emergencyNumber })}</p>
      ) : null}

      <div ref={sinkRef} aria-hidden="true" />
    </section>
  );
}

/**
 * La conversazione scritta.
 *
 * Interrogazione periodica, non un canale aperto: è la stessa scelta del resto
 * della console (O4), e una connessione persistente in più su un VPS singolo
 * andrebbe giustificata con qualcosa che qui non c'è — due secondi di ritardo
 * su un messaggio scritto non cambiano una conversazione.
 */
function ChatThread({ sessionId, context }: { sessionId: string; context: SessionContext | null }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [failed, setFailed] = useState(false);
  const cursorRef = useRef<string | undefined>(undefined);
  const threadRef = useRef<HTMLDivElement>(null);
  /**
   * Un giro alla volta.
   *
   * Questo è l'unico poller della console che **accumula** invece di
   * sostituire l'elenco, e per questo era l'unico che potesse raddoppiare
   * qualcosa. Il cursore si aggiorna solo quando la risposta torna: con un RTT
   * sopra i due secondi — la console è pensata per il telefono — l'intervallo
   * faceva partire un secondo giro con lo **stesso** cursore, il server
   * filtrava sullo stesso istante e rimandava gli stessi messaggi, che
   * venivano accodati una seconda volta. Il risultato erano un thread doppio e
   * chiavi React duplicate, cioè un avviso in console e un riquadro che scorre
   * su una conversazione che non è mai avvenuta così.
   */
  const pollingRef = useRef(false);

  const poll = useCallback(async () => {
    // La guardia: finché il giro precedente non è tornato non se ne apre un
    // altro. Saltare un giro non perde niente — il cursore non si è mosso, e
    // fra due secondi si richiede lo stesso pezzo di conversazione.
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const result = await sessionMessages(sessionId, cursorRef.current);
      if (result.messages.length === 0) return;
      cursorRef.current = result.messages[result.messages.length - 1]?.sentAt;
      setMessages((current) => appendUnseen(current, result.messages));
    } catch {
      // Un giro perso non è una conversazione persa: si riprova fra due
      // secondi, come per la coda della console.
    } finally {
      pollingRef.current = false;
    }
  }, [sessionId]);

  useEffect(() => {
    setMessages([]);
    cursorRef.current = undefined;
    /*
      Anche la guardia si azzera cambiando sessione: un giro rimasto appeso
      alla conversazione precedente terrebbe muta quella nuova.
    */
    pollingRef.current = false;
    void poll();
    const handle = setInterval(() => void poll(), CHAT_POLL_MS);
    return () => clearInterval(handle);
  }, [poll]);

  /**
   * Lo scorrimento segue i messaggi, non il montaggio.
   *
   * Con `[]` scorreva una volta sola: dal messaggio che faceva traboccare il
   * riquadro in poi, quelli nuovi finivano sotto il bordo e il volontario
   * smetteva di vederli senza che niente glielo dicesse.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: `messages` non si legge nel corpo, ma è la ragione per cui l'effetto esiste — toglierlo lo riporterebbe a scorrere una volta sola, che è il difetto per cui la dipendenza è stata aggiunta.
  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const send = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    try {
      const result = await sendSessionMessage(sessionId, body);
      cursorRef.current = result.message.sentAt;
      // Dalla stessa porta, per la stessa ragione: un giro partito prima
      // dell'invio può riportare indietro il messaggio appena scritto.
      setMessages((current) => appendUnseen(current, [result.message]));
      setFailed(false);
    } catch {
      // Il messaggio torna nella casella invece di sparire.
      setDraft(body);
      setFailed(true);
    }
  };

  const earlier = context?.previousMessages ?? [];

  return (
    <div className="chat">
      {earlier.length > 0 ? (
        <details className="chat__earlier">
          <summary>{t('call.earlier_conversations', { count: earlier.length })}</summary>
          {earlier.map((message) => (
            <p key={message.id} className={bubbleClass(message)}>
              {/*
                Chi ha scritto, anche qui. Nel thread vivo c'era e nelle
                conversazioni precedenti no: lì la sola cosa che distingueva i
                due lati erano il colore e l'allineamento (WCAG 1.4.1).
              */}
              <span className="visually-hidden">{`${t(
                message.sender === 'volunteer' ? 'call.from_me' : 'call.from_them',
              )}: `}</span>
              {message.body}
            </p>
          ))}
        </details>
      ) : null}

      {/*
        `role="log"` e basta: porta con sé `aria-live="polite"`, e dichiararlo
        anche a mano è la doppia regione che su alcune versioni di JAWS produce
        una doppia lettura (`docs/DECISIONI.md`, X14). Un `log` annuncia solo
        ciò che si aggiunge, che è il comportamento giusto per una
        conversazione.
      */}
      {/*
        `tabIndex={0}`: un riquadro che scorre deve essere raggiungibile da
        tastiera, altrimenti per rileggere indietro serve il mouse
        (WCAG 2.1.1, regola axe `scrollable-region-focusable`).
      */}
      <div
        ref={threadRef}
        className="chat__thread"
        role="log"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: un riquadro che scorre deve essere raggiungibile da tastiera (WCAG 2.1.1, `scrollable-region-focusable`): senza, per rileggere indietro serve il mouse.
        tabIndex={0}
        aria-label={t('call.thread_label')}
      >
        {messages.map((message) => (
          <p key={message.id} className={bubbleClass(message)}>
            <span className="visually-hidden">{`${t(
              message.sender === 'volunteer' ? 'call.from_me' : 'call.from_them',
            )}: `}</span>
            {message.body}
          </p>
        ))}
      </div>

      <form className="chat__composer" onSubmit={(event) => void send(event)}>
        <label className="visually-hidden" htmlFor="chat-draft">
          {t('call.write_label')}
        </label>
        <textarea
          id="chat-draft"
          rows={2}
          maxLength={MESSAGE_MAX_LENGTH}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('call.write_label')}
        />
        <button type="submit" className="button button--primary">
          {t('call.send')}
        </button>
      </form>
      {/*
        Come sopra: montata prima, vuota, riempita quando c'è qualcosa da dire.
        Un messaggio che non è partito è la sola cosa che distingue "ho
        scritto" da "ha letto", e chi non vede lo scopriva solo tornando
        indietro con lo screen reader.

        **Polite**, come l'esito uguale in `SupervisionPanel`: arriva subito
        dopo un'azione voluta, e interrompere con questo la lettura di un
        messaggio appena arrivato sarebbe un danno netto in una conversazione
        scritta.
      */}
      <p
        className={failed ? 'banner banner--danger' : 'visually-hidden'}
        role="status"
        aria-live="polite"
      >
        {failed ? t('call.not_sent') : ''}
      </p>
    </div>
  );
}

/**
 * Accoda solo ciò che non c'è già, riconoscendolo dall'`id`.
 *
 * La deduplica sta **dentro** l'aggiornamento dello stato e non accanto alla
 * richiesta di proposito: è l'unico punto che vede davvero l'elenco corrente,
 * e due aggiornamenti che partono dallo stesso disegno passano comunque di qui
 * uno dopo l'altro. Una guardia sola — quella sul giro in corso — proteggerebbe
 * dal caso previsto e non dal server che rimanda un messaggio già visto.
 *
 * Se non c'è niente di nuovo si restituisce **lo stesso** array: un array nuovo
 * e identico farebbe scorrere il riquadro in fondo a ogni giro, strappando la
 * lettura a chi era tornato indietro a rileggere.
 */
function appendUnseen(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const known = new Set(current.map((message) => message.id));
  const fresh = incoming.filter((message) => !known.has(message.id));
  return fresh.length === 0 ? current : [...current, ...fresh];
}

function bubbleClass(message: ChatMessage): string {
  return `chat__msg chat__msg--${message.sender === 'volunteer' ? 'me' : 'them'}`;
}

function formatElapsed(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}
